import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

const TURBO_STREAM_MIME = "text/vnd.turbo-stream.html";

// `text/vnd.turbo-stream.html; charset=utf-8` is the same content type as the
// bare one, so only the part before the first `;` decides. Anything else — most
// likely `text/html` from a login or error page — is not a stream and must not
// reach Turbo.
function isTurboStream(response) {
  const contentType = response.headers.get("content-type") || "";

  return contentType.split(";")[0].trim().toLowerCase() === TURBO_STREAM_MIME;
}

// Loads the next batch of a list card when the reader reaches the end of the
// current one (Max, 2026-08-08). Replaces the hard limits the dashboard services
// used to apply, which made everything past row four unreachable.
//
// The controller sits on a sentinel element that is the LAST item inside the
// list. The server answers with a Turbo Stream that appends the next rows before
// the sentinel and then either moves it forward or removes it — so this file only
// ever decides *when* to ask, never what the rows look like.
//
// Two different things scroll depending on the breakpoint: on desktop the list
// itself is capped and scrolls inside the card, on mobile the card is uncapped
// and the page scrolls. Both are covered by observing the nearest scrolling
// ancestor: an element clipped by an overflow container does not intersect the
// viewport either, so a plain viewport observer would never fire on desktop.
export default class extends Controller {
  static values = { url: String };
  static targets = ["failure"];

  connect() {
    this.connected = true;
    this.loading = false;
    this.failed = false;
    this.request = null;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.load();
      },
      // A little lead time so the next batch is usually there by the time the
      // reader arrives at the end of the current one.
      { root: this.scrollRoot(), rootMargin: "200px" }
    );
    this.observer.observe(this.element);
  }

  disconnect() {
    this.connected = false;
    this.observer?.disconnect();
    this.observer = null;
    // A batch still in flight belongs to the list this marker was part of. The
    // marker ids are reused across lists, ranges and programs, so an answer that
    // arrives after the reader has moved on would replace the *new* marker with
    // the *old* list's rows.
    this.request?.abort();
    this.request = null;
    this.loading = false;
  }

  // The nearest ancestor that actually clips its overflow. `null` falls back to
  // the viewport, which is the right root when nothing in between scrolls.
  scrollRoot() {
    let node = this.element.parentElement;

    while (node && node !== document.body) {
      const overflowY = getComputedStyle(node).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") return node;
      node = node.parentElement;
    }

    return null;
  }

  async load() {
    if (this.loading || this.failed || !this.hasUrlValue) return;

    this.loading = true;
    // Stop watching while the request is in flight: without this, every scroll
    // event on the way to the end fires another identical request.
    this.observer?.unobserve(this.element);

    const request = new AbortController();
    this.request = request;
    const element = this.element;

    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: TURBO_STREAM_MIME },
        credentials: "same-origin",
        signal: request.signal,
      });

      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
      // A redirect answers 200 with a page, not with a stream — an expired
      // session is the common case. `ok` alone would let that through.
      if (response.redirected) throw new Error("redirected");
      if (!isTurboStream(response)) throw new Error("not a turbo stream");

      const message = await response.text();
      // The body can arrive after `abort()` no longer helps: once the response
      // has resolved, aborting cannot take it back. This is the second half of
      // the same guard, and the one that decides whether the rows are still
      // wanted here.
      if (!this.current(request, element)) return;

      Turbo.renderStreamMessage(message);
    } catch (error) {
      // Aborting is how navigation ends a request, not a failure to report.
      if (error.name === "AbortError") return;
      if (!this.current(request, element)) return;

      this.fail();
    } finally {
      if (this.request === request) {
        this.request = null;
        this.loading = false;
      }
    }
  }

  // True only while the answer still belongs here: this controller instance is
  // connected, the request is still the one it started, and its marker is still
  // the element carrying that id in the document.
  current(request, element) {
    return (
      this.connected &&
      this.request === request &&
      this.element === element &&
      element.isConnected &&
      (!element.id || document.getElementById(element.id) === element)
    );
  }

  // A failed batch leaves the marker visible. Watching it again here would turn
  // a permanent 4xx/5xx or an offline device into an endless fetch → catch →
  // observe → fetch loop, so the way on is the retry control instead.
  fail() {
    this.failed = true;
    if (this.hasFailureTarget) this.failureTarget.hidden = false;
  }

  retry(event) {
    event?.preventDefault();
    if (this.loading) return;

    this.failed = false;
    if (this.hasFailureTarget) this.failureTarget.hidden = true;
    this.load();
  }
}
